import React, { useState } from "react";
import {
  View,
  StyleSheet,
  SafeAreaView,
  FlatList,
  ActivityIndicator,
  RefreshControl,
  TouchableOpacity,
} from "react-native";
import { router } from "expo-router";
import { AppText } from "@/components/AppText";
import Header from "@/components/ui/Header";
import { FontFamilies } from "@/constants/Typography";
import useEligibleWithdrawals from "@/hooks/useEligibleWithdrawals";
import useBatchProcessPayout from "@/hooks/useBatchProcessPayout";
import WithdrawalCard from "./components/WithdrawalCard";
import BatchProcessConfirmationModal from "./components/BatchProcessConfirmationModal";

const AdminPayoutScreen: React.FC = () => {
  const { eligibleWithdrawals, isLoading, isError, refetch } =
    useEligibleWithdrawals();
  const { isProcessing, processBatch } = useBatchProcessPayout();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleProcessAllPayouts = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmProcess = async () => {
    const success = await processBatch(eligibleWithdrawals);
    if (success) {
      refetch(); // Refresh the list after successful processing
    }
    setShowConfirmModal(false);
  };

  const handleBackPress = () => {
    router.dismissTo("/(tabs)/feeds");
  };

  if (isLoading && eligibleWithdrawals.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <Header title="Admin Payout" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#0F9129" />
          <AppText style={styles.loadingText}>
            Loading eligible withdrawals...
          </AppText>
        </View>
      </SafeAreaView>
    );
  }

  if (isError && eligibleWithdrawals.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <Header title="Admin Payout" />
        <View style={styles.errorContainer}>
          <AppText style={styles.errorText}>
            Failed to load eligible withdrawals
          </AppText>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <Header title="Admin Payout" onBackPress={handleBackPress} />
      <View style={styles.content}>
        <View style={styles.headerSection}>
          <View style={styles.titleContainer}>
            <AppText style={styles.title}>Eligible Withdrawals</AppText>
            <AppText style={styles.subtitle}>
              Total: {eligibleWithdrawals.length} users eligible for payout
            </AppText>
          </View>

          {eligibleWithdrawals.length > 0 && (
            <TouchableOpacity
              style={[
                styles.processButton,
                isProcessing && styles.processButtonDisabled,
              ]}
              onPress={handleProcessAllPayouts}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <AppText style={styles.processButtonText}>Process All</AppText>
              )}
            </TouchableOpacity>
          )}
        </View>

        <FlatList
          data={eligibleWithdrawals}
          renderItem={({ item }) => <WithdrawalCard withdrawal={item} />}
          keyExtractor={(item) => item.userId}
          style={styles.list}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={isLoading}
              onRefresh={refetch}
              colors={["#0F9129"]}
              tintColor="#0F9129"
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <AppText style={styles.emptyText}>
                No eligible withdrawals found
              </AppText>
            </View>
          }
        />
      </View>

      <BatchProcessConfirmationModal
        visible={showConfirmModal}
        count={eligibleWithdrawals.length}
        isProcessing={isProcessing}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmProcess}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F8FAFC",
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 20,
  },
  headerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    fontSize: 24,
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#161D14",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(22, 29, 20, 0.7)",
    lineHeight: 23,
    letterSpacing: -0.3,
  },
  list: {
    flex: 1,
  },
  processButton: {
    backgroundColor: "#0F9129",
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: "center",
    marginLeft: 16,
  },
  processButtonDisabled: {
    opacity: 0.5,
  },
  processButtonText: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#FFFFFF",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
  },
  loadingText: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(22, 29, 20, 0.7)",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
  },
  errorText: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#DC2626",
    textAlign: "center",
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 40,
  },
  emptyText: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(22, 29, 20, 0.5)",
    textAlign: "center",
  },
});

export default AdminPayoutScreen;
